import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { BRAND, GRAY } from "../colors";

const LANGUAGES = [
  {
    flag: "🇬🇧",
    lang: "English",
    review: '"Best Italian in London! The pasta was incredible."',
    reply: "Thank you for dining at La Dolce Vita in Covent Garden! We're so glad you loved our handmade pasta.",
  },
  {
    flag: "🇪🇸",
    lang: "Español",
    review: '"¡La mejor pasta de Londres! Increíble."',
    reply: "¡Gracias por visitarnos en La Dolce Vita, Covent Garden! Nos alegra que haya disfrutado de nuestra pasta artesanal.",
  },
  {
    flag: "🇩🇪",
    lang: "Deutsch",
    review: '"Beste Pasta in London! Einfach fantastisch."',
    reply: "Danke für Ihren Besuch bei La Dolce Vita in Covent Garden! Wir freuen uns, dass Ihnen unsere Pasta geschmeckt hat.",
  },
  {
    flag: "🇯🇵",
    lang: "日本語",
    review: '"ロンドンで最高のイタリアン！パスタが絶品。"',
    reply: "La Dolce Vita Covent Gardenへのご来店ありがとうございます！手作りパスタを楽しんでいただけて嬉しいです。",
  },
];

const CYCLE_FRAMES = 38;

export const MultiLanguageAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const langIndex = Math.floor(frame / CYCLE_FRAMES) % LANGUAGES.length;
  const localFrame = frame % CYCLE_FRAMES;

  const fadeIn = interpolate(localFrame, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const fadeOut = interpolate(localFrame, [30, 38], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const opacity = Math.min(fadeIn, fadeOut);
  const slideY = interpolate(fadeIn, [0, 1], [15, 0]);

  const lang = LANGUAGES[langIndex];
  const enterScale = spring({ frame: localFrame, fps, config: { damping: 14 } });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: GRAY[50],
        justifyContent: "center",
        alignItems: "center",
        padding: 48,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 540,
          opacity,
          transform: `translateY(${slideY}px)`,
        }}
      >
        {/* Language badge */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
            justifyContent: "center",
          }}
        >
          <span style={{ fontSize: 36 }}>{lang.flag}</span>
          <span
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: BRAND[600],
              transform: `scale(${enterScale})`,
              display: "inline-block",
            }}
          >
            {lang.lang}
          </span>
          <div
            style={{
              fontSize: 14,
              color: BRAND[500],
              backgroundColor: BRAND[50],
              padding: "4px 12px",
              borderRadius: 12,
              fontWeight: 600,
            }}
          >
            Auto-detected
          </div>
        </div>

        {/* Review */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: 16,
            padding: "18px 24px",
            marginBottom: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            border: `1px solid ${GRAY[200]}`,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 600, color: GRAY[400], marginBottom: 6, letterSpacing: 1 }}>
            REVIEW
          </div>
          <div style={{ fontSize: 20, color: GRAY[800], lineHeight: 1.5 }}>
            <span style={{ color: "#F59E0B" }}>★★★★★</span> {lang.review}
          </div>
        </div>

        {/* Reply */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: 16,
            padding: "18px 24px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            border: `1px solid ${BRAND[200]}`,
            borderLeftWidth: 4,
            borderLeftColor: BRAND[500],
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 600, color: BRAND[600], marginBottom: 6, letterSpacing: 1 }}>
            AI REPLY
          </div>
          <div style={{ fontSize: 20, color: GRAY[700], lineHeight: 1.5 }}>{lang.reply}</div>
        </div>
      </div>

      {/* Language dots */}
      <div
        style={{
          position: "absolute",
          bottom: 36,
          display: "flex",
          gap: 8,
        }}
      >
        {LANGUAGES.map((l, i) => (
          <div
            key={l.lang}
            style={{
              width: i === langIndex ? 24 : 10,
              height: 10,
              borderRadius: 5,
              backgroundColor: i === langIndex ? BRAND[500] : GRAY[300],
            }}
          />
        ))}
      </div>
    </AbsoluteFill>
  );
};
